package features.teams.handlers

import features.teams.domain.Team
import features.schedule.domain.Schedule
import AppState
import Router
import shared.ui.ModalManager

// Team Navigation and Management
fun openTeam(teamId: String) {
    console.log("🏢 openTeam called with teamId:", teamId)
    AppState.currentTeam = AppState.teams.find { it.id == teamId }
    console.log("📌 Current team set to:", AppState.currentTeam?.name)
    AppState.currentTab = "members" // Reset to members tab
    
    // Update URL to include team slug and current tab
    val teamSlug = AppState.currentTeam?.slug ?: teamId
    kotlinx.browser.window.history.pushState(null, "", "/team/$teamSlug#${AppState.currentTab}")
    
    // Navigate to team-detail page first (will show loading state)
    AppState.isLoading = true
    Router.navigateTo("team-detail")
    
    // Load team data from Firestore
    if (AppState.currentTeam != null) {
        console.log("✅ Current team is not null, loading data...")
        
        // Initialize mock data for demo purposes (overtime, time off, holidays, leave types)
        // NOTE: Attendance is now loaded from Firebase
        AppState.initMockDataForTeam(teamId)
        
        // Load members
        console.log("👥 Calling features.members.data.MemberRepository.loadMembers for team:", teamId)
        features.members.data.MemberRepository.loadMembers(teamId)
            .then { members ->
                console.log("✅ loadMembers promise resolved with", members.size, "members")
                AppState.members[teamId] = members.toMutableList()
                console.log("💾 Saved to AppState.members[$teamId]")
                
                // Load attendance with role-based filtering
                val currentUserId = AppState.currentUser?.id ?: ""
                val userRole = features.attendance.helpers.AttendanceHelpers.getUserRole(currentUserId, teamId)
                console.log("🔍 log-attendance: Starting attendance load")
                console.log("🔍 log-attendance: Current user ID:", currentUserId)
                console.log("🔍 log-attendance: User role:", userRole)
                console.log("🔍 log-attendance: Team ID:", teamId)
                
                features.attendance.data.AttendanceRepository.getAttendanceRecordsForUser(teamId, currentUserId, userRole)
                    .then { attendanceRecords ->
                        console.log("✅ log-attendance: Firebase query successful")
                        console.log("✅ log-attendance: Loaded ${attendanceRecords.size} attendance records from Firebase")
                        console.log("✅ log-attendance: Records:", attendanceRecords)
                        AppState.attendance[teamId] = attendanceRecords.toMutableList()
                        console.log("✅ log-attendance: Saved to AppState.attendance[$teamId]")
                        console.log("✅ log-attendance: AppState.attendance[$teamId] size:", AppState.attendance[teamId]?.size)
                        
                        // Load shifts
                        console.log("⏰ Loading shifts...")
                        features.shifts.data.ShiftRepository.loadShifts(teamId)
                            .then { shifts ->
                                console.log("✅ loadShifts resolved with", shifts.size, "shifts")
                                AppState.shiftTemplates[teamId] = shifts.toMutableList()
                                
                                // Load schedule for current month
                                val now = kotlin.js.Date()
                                val year = now.getFullYear().toInt()
                                val month = (now.getMonth() + 1).toInt()
                                
                                console.log("📅 Loading schedule for $year-$month...")
                                features.schedule.data.ScheduleRepository.loadSchedule(teamId, year, month)
                                    .then { schedule ->
                                        if (schedule != null) {
                                            AppState.schedules[teamId] = schedule
                                            console.log("✅ Schedule loaded")
                                        }
                                        AppState.isLoading = false
                                        console.log("🔄 Re-rendering page with loaded data...")
                                        Router.render() // Re-render to show loaded data
                                    }
                                    .catch { error ->
                                        console.error("❌ Error loading schedule:", error)
                                        AppState.isLoading = false
                                        Router.render() // Re-render even on error
                                    }
                            }
                            .catch { error ->
                                console.error("❌ Error loading shifts:", error)
                                AppState.isLoading = false
                                Router.render() // Re-render even on error
                            }
                    }
                    .catch { error ->
                        console.error("❌ log-attendance: Error loading attendance:", error)
                        console.error("❌ log-attendance: Error details:", error.message)
                        // Continue loading other data even if attendance fails
                        
                        // Load shifts
                        console.log("⏰ Loading shifts...")
                        features.shifts.data.ShiftRepository.loadShifts(teamId)
                            .then { shifts ->
                                console.log("✅ loadShifts resolved with", shifts.size, "shifts")
                                AppState.shiftTemplates[teamId] = shifts.toMutableList()
                                
                                // Load schedule for current month
                                val now = kotlin.js.Date()
                                val year = now.getFullYear().toInt()
                                val month = (now.getMonth() + 1).toInt()
                                
                                console.log("📅 Loading schedule for $year-$month...")
                                features.schedule.data.ScheduleRepository.loadSchedule(teamId, year, month)
                                    .then { schedule ->
                                        if (schedule != null) {
                                            AppState.schedules[teamId] = schedule
                                            console.log("✅ Schedule loaded")
                                        }
                                        AppState.isLoading = false
                                        console.log("🔄 Re-rendering page with loaded data...")
                                        Router.render() // Re-render to show loaded data
                                    }
                                    .catch { error ->
                                        console.error("❌ Error loading schedule:", error)
                                        AppState.isLoading = false
                                        Router.render() // Re-render even on error
                                    }
                            }
                            .catch { error ->
                                console.error("❌ Error loading shifts:", error)
                                AppState.isLoading = false
                                Router.render() // Re-render even on error
                            }
                    }
            }
            .catch { error ->
                console.error("❌ Error loading members:", error)
                console.error("Error details:", error.asDynamic().message)
                AppState.isLoading = false
                Router.render() // Re-render even on error
            }
    } else {
        console.log("⚠️ Current team is null")
        AppState.isLoading = false
    }
}

fun switchTab(tabId: String) {
    AppState.currentTab = tabId
    
    // Update URL with team slug and hash to preserve state on refresh
    val teamSlug = AppState.currentTeam?.slug ?: AppState.currentTeam?.id ?: ""
    if (teamSlug.isNotEmpty()) {
        kotlinx.browser.window.history.replaceState(null, "", "/team/$teamSlug#$tabId")
    } else {
        // Fallback: just update hash if no team is set
        kotlinx.browser.window.location.hash = "#$tabId"
    }
    
    // Load schedule data when switching to schedule tab
    if (tabId == "schedule") {
        val teamId = AppState.currentTeam?.id
        if (teamId != null && AppState.schedules[teamId] == null) {
            // Load current month schedule
            val now = js("new Date()")
            val year = now.getFullYear() as Int
            val month = (now.getMonth() as Int) + 1 // JS months are 0-indexed
            
            console.log("Loading schedule for $year-$month")
            features.schedule.data.ScheduleRepository.loadSchedule(teamId, year, month)
                .then { schedule ->
                    if (schedule != null) {
                        AppState.schedules[teamId] = schedule
                        console.log("✅ Schedule loaded")
                    } else {
                        console.log("ℹ️ No schedule found, creating empty schedule")
                        // Create empty schedule
                        AppState.schedules[teamId] = Schedule(
                            year = year,
                            month = month,
                            assignments = mutableMapOf()
                        )
                    }
                    Router.render()
                }
                .catch { error ->
                    console.error("Error loading schedule:", error)
                    Router.render()
                }
            return // Don't render yet, wait for schedule to load
        }
    }
    
    Router.render()
}

// Initialize tab from URL hash on page load
fun initTabFromHash() {
    val hash = kotlinx.browser.window.location.hash
    if (hash.isNotEmpty() && hash.startsWith("#")) {
        val tabId = hash.substring(1) // Remove the # symbol
        // Valid tabs
        val validTabs = listOf("members", "shifts", "schedule", "attendance", "overtime", "timeoff", "settings")
        if (tabId in validTabs) {
            AppState.currentTab = tabId
        }
    }
}

// Team Settings Functions
fun showEditTeamNameModal(team: Team) {
    ModalManager.show("edit-team-name-modal")
}

fun showEditTeamAvatarModal(team: Team) {
    ModalManager.show("edit-team-avatar-modal")
}

fun showArchiveTeamModal(team: Team) {
    val confirmed = kotlinx.browser.window.confirm(
        "Are you sure you want to archive '${team.name}'?\\n\\n" +
        "Archived teams are read-only. Members will not be able to make any changes."
    )
    
    if (confirmed) {
        archiveTeam(team.id)
    }
}

fun archiveTeam(teamId: String) {
    console.log("Archiving team:", teamId)
    features.teams.data.TeamRepository.archiveTeam(teamId)
        .then {
            console.log("✅ Team archived successfully")
            // Reload teams
            val userId = AppState.currentUser?.id ?: return@then
            AppState.teams.clear()
            AppState.teamsLoaded = false
            features.teams.data.TeamRepository.loadUserTeams(userId)
                .then { teams ->
                    AppState.teams.addAll(teams)
                    AppState.teamsLoaded = true
                    // Update current team
                    AppState.currentTeam = teams.find { it.id == teamId }
                    Router.render()
                }
        }
        .catch { error ->
            console.error("Error archiving team:", error)
            kotlinx.browser.window.alert("Failed to archive team")
        }
}

fun unarchiveTeam(teamId: String) {
    console.log("Unarchiving team:", teamId)
    features.teams.data.TeamRepository.unarchiveTeam(teamId)
        .then {
            console.log("✅ Team unarchived successfully")
            // Reload teams
            val userId = AppState.currentUser?.id ?: return@then
            AppState.teams.clear()
            AppState.teamsLoaded = false
            features.teams.data.TeamRepository.loadUserTeams(userId)
                .then { teams ->
                    AppState.teams.addAll(teams)
                    AppState.teamsLoaded = true
                    // Update current team
                    AppState.currentTeam = teams.find { it.id == teamId }
                    Router.render()
                }
        }
        .catch { error ->
            console.error("Error unarchiving team:", error)
            kotlinx.browser.window.alert("Failed to unarchive team")
        }
}

fun showCreateTeamModal() {
    ModalManager.show("create-team-modal")
}
